'use client';

import { MouseEvent } from 'react';
import clsx from 'classnames';
import { Loader } from '../loader';

import { ButtonProps } from './types';
import styles from './Button.module.scss';

function hexToRGB(hex: string, opacity: number = 1) {
	let r = '0';
	let g = '0';
	let b = '0';

	// 3 digits
	if (hex.length == 4) {
		r = '0x' + hex[1] + hex[1];
		g = '0x' + hex[2] + hex[2];
		b = '0x' + hex[3] + hex[3];

		// 6 digits
	} else if (hex.length == 7) {
		r = '0x' + hex[1] + hex[2];
		g = '0x' + hex[3] + hex[4];
		b = '0x' + hex[5] + hex[6];
	}

	return `rgba(${+r},${+g},${+b},${opacity})`;
}

export const Button = ({
	variant = 'primary',
	size = 'big',
	startIcon,
	endIcon,
	isLoading = false,
	fullWidth = false,
	as = 'button',
	href,
	prefetch = false,
	type,
	style = {},
	buttonName,
	target,
	background,
	color,
	rel,
	loaderSize = 30,
	loaderStroke = 3,
	...props
}: ButtonProps) => {
	const { children } = props;
	const Component = as;

	return (
		<Component
			href={href}
			prefetch={href ? prefetch || false : undefined}
			className={clsx(
				`Button`,
				styles.button,
				{
					[styles[`button--${variant}`]]: !!variant,
					[styles[`button--${size}`]]: !!size,
					[styles['button--fullWidth']]: !!fullWidth,
					[styles[`button--${variant}--disabled`]]: props.isDisabled,
				},
				buttonName
			)}
			type={type}
			onClick={(event: MouseEvent) => {
				if (as === 'button' && type !== 'submit') {
					event.preventDefault();
				}

				props?.onPress?.(event);
			}}
			style={{
				...style,
				...(background
					? {
							'--button-custom-background': `${hexToRGB(background)}`,
							'--button-custom-background-hover': `${hexToRGB(background, 0.5)}`,
					  }
					: {}),
				...(color ? { '--button-custom-color': color } : {}),
			}}
			target={target}
			rel={rel}
			disabled={props.isDisabled}
		>
			{isLoading ? (
				<Loader
					backgroundColor="transparent"
					color={props.isDisabled ? '#fff' : 'currentcolor'}
					size={loaderSize}
					stroke={loaderStroke}
				/>
			) : (
				<>
					{startIcon && (
						<div className={clsx(`Button__StartIcon`, styles.button__startIcon)}>{startIcon}</div>
					)}
					{children}
					{endIcon && (
						<div className={clsx(`Button__EndIcon`, styles.button__endIcon)}>{endIcon}</div>
					)}
				</>
			)}
		</Component>
	);
};
